import React, { useState, useEffect } from "react";
import AdminLayout from "../../components/AdminLayout";
import Modal from "../../components/Modal";
import api from "../../services/api";
import { toast } from "react-toastify";
import { FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";

const StatusManagement = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [formData, setFormData] = useState({
    serial_number: "",
    exchange: "",
    status: "",
    remarks: "",
    display_order: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      const response = await api.get("/dashboard-status/admin");
      setStatuses(response.data);
    } catch (error) {
      toast.error("Failed to fetch status entries");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (status = null) => {
    if (status) {
      setEditingStatus(status);
      setFormData({
        serial_number: status.serial_number,
        exchange: status.exchange,
        status: status.status,
        remarks: status.remarks || "",
        display_order: status.display_order || 0,
        is_active: status.is_active,
      });
    } else {
      setEditingStatus(null);
      const nextSerial =
        statuses.length > 0
          ? Math.max(...statuses.map((s) => s.serial_number)) + 1
          : 1;
      setFormData({
        serial_number: nextSerial,
        exchange: "",
        status: "",
        remarks: "",
        display_order: statuses.length,
        is_active: true,
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingStatus(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingStatus) {
        await api.put(`/dashboard-status/${editingStatus.id}`, formData);
        toast.success("Status entry updated successfully");
      } else {
        await api.post("/dashboard-status", formData);
        toast.success("Status entry created successfully");
      }
      handleCloseModal();
      fetchStatuses();
    } catch (error) {
      toast.error(error.response?.data?.message || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this status entry?")) {
      try {
        await api.delete(`/dashboard-status/${id}`);
        toast.success("Status entry deleted successfully");
        fetchStatuses();
      } catch (error) {
        toast.error("Failed to delete status entry");
      }
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="spinner"></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="page-header">
        <h1 className="page-title">Dashboard Status Management</h1>
        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
          <FiPlus />
          Add Status Entry
        </button>
      </div>

      <div className="card">
        <p style={{ marginBottom: "1rem", color: "#666" }}>
          This table is displayed on all Members dashboards. Manage exchange
          status information here.
        </p>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Exchange</th>
                <th>Status</th>
                <th>Remarks</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {statuses.map((status) => (
                <tr key={status.id}>
                  <td>{status.serial_number}</td>
                  <td>{status.exchange}</td>
                  <td>
                    <span
                      className={`badge ${
                        status.status.toLowerCase() === "active"
                          ? "badge-success"
                          : status.status.toLowerCase() === "inactive"
                          ? "badge-danger"
                          : "badge-warning"
                      }`}
                    >
                      {status.status}
                    </span>
                  </td>
                  <td>{status.remarks || "-"}</td>
                  <td>
                    <span
                      className={`badge ${
                        status.is_active ? "badge-success" : "badge-danger"
                      }`}
                    >
                      {status.is_active ? "Yes" : "No"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="action-btn edit"
                      onClick={() => handleOpenModal(status)}
                      title="Edit"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(status.id)}
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
              {statuses.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">
                    No status entries found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingStatus ? "Edit Status Entry" : "Add Status Entry"}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Serial Number *</label>
            <input
              type="number"
              name="serial_number"
              value={formData.serial_number}
              onChange={handleInputChange}
              required
              min="1"
            />
          </div>

          <div className="form-group">
            <label>Exchange *</label>
            <input
              type="text"
              name="exchange"
              value={formData.exchange}
              onChange={handleInputChange}
              placeholder="e.g., Binance, Coinbase"
              required
            />
          </div>

          <div className="form-group">
            <label>Status *</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Pending">Pending</option>
              <option value="Maintenance">Maintenance</option>
            </select>
          </div>

          <div className="form-group">
            <label>Remarks</label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleInputChange}
              rows="3"
              placeholder="Additional notes or remarks"
            />
          </div>

          <div className="form-group">
            <label>Display Order</label>
            <input
              type="number"
              name="display_order"
              value={formData.display_order}
              onChange={handleInputChange}
              min="0"
            />
          </div>

          <div className="py-4">
            <label className="visibility-option">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
              Show on Members Dashboard
            </label>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseModal}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {editingStatus ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </Modal>
    </AdminLayout>
  );
};

export default StatusManagement;
